import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import type { ApiResponse, PaginatedResponse } from '@/types'

export type AssetClass =
  | 'mechanical'
  | 'electrical'
  | 'building'
  | 'it'
  | 'vehicle'
  | 'tool'
  | 'other'

export type AssetStatus =
  | 'draft'
  | 'active'
  | 'in_service'
  | 'under_maintenance'
  | 'out_of_service'
  | 'disposed'
  | 'lost'

export interface AssetRecord {
  id: number
  asset_code: string
  name: string
  description?: string
  asset_class?: AssetClass | string
  status?: AssetStatus | string
  criticality?: string
  ownership_type?: string
  client_user?: number | null
  customer_name?: string | null
  location?: number | null
  location_name?: string | null
  category?: number | null
  category_name?: string | null
  parent_asset?: number | null
  tag_number?: string
  serial_number?: string
  barcode?: string
  manufacturer?: string
  model_name?: string
  install_date?: string | null
  warranty_end?: string | null
  next_pm_due_at?: string | null
  last_serviced_at?: string | null
  photo_path?: string
  is_overdue?: boolean
  condition_score?: number | null
  condition_rating?: string
  purchase_cost?: string | number | null
  purchase_date?: string | null
  depreciation_method?: string
  salvage_value?: string | number | null
  useful_life_months?: number | null
  finance_code?: string
  active?: boolean
  updated_at?: string
}

export interface AssetWritePayload {
  asset_code?: string
  name: string
  description?: string
  tag_number?: string
  serial_number?: string
  barcode?: string
  asset_class?: string
  status?: string
  criticality?: string
  ownership_type?: string
  client_user?: number | string | null
  category?: number | string | null
  location?: number | string | null
  manufacturer?: string
  model_name?: string
  install_date?: string | null
  warranty_end?: string | null
  vendor_name?: string
  active?: boolean
}

export interface MaintenancePlanRecord {
  id: number
  asset: number
  asset_code?: string
  asset_name?: string
  name: string
  work_type?: string
  frequency_rule?: string
  interval_days?: number | null
  reminder_lead_days?: number
  auto_generate_job?: boolean
  next_due_at?: string | null
  last_completed_at?: string | null
  active?: boolean
  notes?: string
}

export interface AssetServiceHistoryRecord {
  id: number
  asset: number
  job: number
  job_ref?: string | null
  job_title?: string | null
  work_type?: string
  outcome?: string
  started_at?: string | null
  completed_at?: string | null
  findings?: string
  resolution?: string
  labor_cost?: string | number | null
}

export interface JobAssetRecord {
  id: number
  job: number
  job_ref?: string | null
  job_title?: string | null
  job_status?: string | null
  asset: number
  asset_code?: string
  asset_name?: string
  asset_status?: string
  last_serviced_at?: string | null
  role?: string
  notes?: string
}

export interface OrderItemAssetRecord {
  id: number
  order_item: number
  asset: number
  asset_code?: string
  asset_name?: string
  role?: string
  notes?: string
}

export interface AssetLocationRecord {
  id: number
  name: string
  code?: string
  parent?: number | null
  client_user?: number | null
}

export interface AssetCategoryRecord {
  id: number
  name: string
  asset_class?: string
}

export interface AssetContractRecord {
  id: number
  name: string
  contract_code?: string
  status?: string
  client_user?: number | null
  start_date?: string | null
  end_date?: string | null
  response_hours?: number
  resolution_hours?: number
  coverage_notes?: string
  active?: boolean
  covered_asset_ids?: number[]
}

export interface AssetBomItemRecord {
  id: number
  asset?: number | null
  category?: number | null
  sku?: string
  part_name: string
  description?: string
  quantity?: string | number
  unit?: string
  unit_cost?: string | number | null
  is_critical?: boolean
}

export interface AssetTransferRecord {
  id: number
  asset: number
  asset_code?: string
  asset_name?: string
  status?: string
  from_location?: number | null
  to_location?: number | null
  from_client_user?: number | null
  to_client_user?: number | null
  from_custodian_name?: string
  to_custodian_name?: string
  transferred_at?: string | null
  notes?: string
  created_at?: string
}

export interface AssetIoTSourceRecord {
  id: number
  asset: number
  meter?: number | null
  external_device_id: string
  provider?: string
  last_payload_at?: string | null
  last_value?: string | number | null
  active?: boolean
}

export interface AssetInspectionRecord {
  id: number
  asset: number
  job?: number | null
  inspected_at?: string
  score: number
  condition_rating?: string
  findings?: string
  recommendations?: string
  inspector_name?: string
}

export interface AssetFinanceSummary {
  asset_id: number
  purchase_cost?: string | null
  salvage_value?: string | null
  book_value?: string | null
  accumulated_depreciation?: string | null
  depreciation_method?: string
  useful_life_months?: number | null
  months_elapsed?: number
  finance_code?: string
  as_of?: string
  computable?: boolean
  notes?: string
}

type Envelope<T> = ApiResponse<T> | T

const unwrap = <T,>(payload: Envelope<T>): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as ApiResponse<T>).data
  }
  return payload as T
}

const toPaginated = <T,>(payload: unknown): PaginatedResponse<T> => {
  if (Array.isArray(payload)) {
    return { count: payload.length, next: null, previous: null, results: payload as T[] }
  }
  const data = payload as PaginatedResponse<T> | undefined
  return {
    count: data?.count ?? data?.results?.length ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    results: Array.isArray(data?.results) ? data.results : [],
  }
}

export const assetsApi = {
  list: async (filters: Record<string, string | number | boolean | undefined> = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return
      params.append(key, String(value))
    })
    const response = await api.get<Envelope<PaginatedResponse<AssetRecord> | AssetRecord[]>>(
      API_PATHS.assets.list,
      { params },
    )
    return toPaginated<AssetRecord>(unwrap(response.data))
  },

  get: async (id: string | number) => {
    const response = await api.get<Envelope<AssetRecord>>(API_PATHS.assets.detail(id))
    return unwrap(response.data)
  },

  create: async (payload: AssetWritePayload) => {
    const response = await api.post<Envelope<AssetRecord>>(API_PATHS.assets.create, payload)
    return unwrap(response.data)
  },

  update: async (id: string | number, payload: Partial<AssetWritePayload>) => {
    const response = await api.post<Envelope<AssetRecord>>(API_PATHS.assets.update(id), payload)
    return unwrap(response.data)
  },

  remove: async (id: string | number) => {
    await api.delete(API_PATHS.assets.delete(id))
  },

  history: async (id: string | number) => {
    const response = await api.get<Envelope<AssetServiceHistoryRecord[]>>(API_PATHS.assets.history(id))
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  createWorkOrder: async (
    id: string | number,
    payload: {
      work_type?: string
      title?: string
      description?: string
      scheduled_start?: string
      scheduled_end?: string
      plan_id?: number
    } = {},
  ) => {
    const response = await api.post<Envelope<{ job_id: number; job_ref?: string; title?: string; status?: string }>>(
      API_PATHS.assets.createWorkOrder(id),
      payload,
    )
    return unwrap(response.data)
  },

  listPlans: async (filters: { asset?: string | number; active?: boolean } = {}) => {
    const params = new URLSearchParams()
    if (filters.asset) params.append('asset', String(filters.asset))
    if (filters.active !== undefined) params.append('active', String(filters.active))
    const response = await api.get<Envelope<MaintenancePlanRecord[]>>(API_PATHS.assets.plans, { params })
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  createPlan: async (payload: Partial<MaintenancePlanRecord> & { asset: number; name: string }) => {
    const response = await api.post<Envelope<MaintenancePlanRecord>>(API_PATHS.assets.planCreate, payload)
    return unwrap(response.data)
  },

  generateNextPlan: async (planId: string | number) => {
    const response = await api.post(API_PATHS.assets.planGenerateNext(planId), {})
    return unwrap(response.data)
  },

  listOverdue: async () => {
    const response = await api.get<Envelope<MaintenancePlanRecord[]>>(API_PATHS.assets.maintenanceOverdue)
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  listUpcoming: async (days = 14) => {
    const response = await api.get<Envelope<MaintenancePlanRecord[]>>(API_PATHS.assets.maintenanceUpcoming, {
      params: { days },
    })
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  listInProgressJobs: async () => {
    const response = await api.get<Envelope<JobAssetRecord[]>>(API_PATHS.assets.maintenanceInProgress)
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  listChildren: async (id: string | number) => {
    const response = await api.get<Envelope<AssetRecord[]>>(API_PATHS.assets.children(id))
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  setParent: async (id: string | number, parentAsset: number | null) => {
    const response = await api.post<Envelope<AssetRecord>>(API_PATHS.assets.setParent(id), {
      parent_asset: parentAsset,
    })
    return unwrap(response.data)
  },

  createLocation: async (payload: { name: string; code?: string; parent?: number | null; client_user?: number | null }) => {
    const response = await api.post(API_PATHS.assets.locationCreate, payload)
    return unwrap(response.data)
  },

  listJobAssets: async (jobId: string | number) => {
    const response = await api.get<Envelope<JobAssetRecord[]>>(API_PATHS.assets.jobAssets, {
      params: { job: jobId },
    })
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  linkJobAssets: async (payload: {
    job: string | number
    asset_ids: number[]
    replace?: boolean
    role?: string
  }) => {
    const response = await api.post<Envelope<JobAssetRecord[]>>(API_PATHS.assets.jobAssetsLink, payload)
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  listOrderItemAssets: async (orderItemId: string | number) => {
    const response = await api.get<Envelope<OrderItemAssetRecord[]>>(API_PATHS.assets.orderItemAssets, {
      params: { order_item: orderItemId },
    })
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  linkOrderItemAssets: async (payload: {
    order_item: string | number
    asset_ids: number[]
    replace?: boolean
    role?: string
  }) => {
    const response = await api.post<Envelope<OrderItemAssetRecord[]>>(API_PATHS.assets.orderItemAssetsLink, payload)
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  listLocations: async () => {
    const response = await api.get<Envelope<AssetLocationRecord[]>>(API_PATHS.assets.locations)
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  listCategories: async () => {
    const response = await api.get<Envelope<AssetCategoryRecord[]>>(API_PATHS.assets.categories)
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  listCustomerAssets: async (userId: string | number) => {
    const response = await api.get<Envelope<AssetRecord[]>>(API_PATHS.assets.customerAssets(userId))
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  listMeters: async (assetId: string | number) => {
    const response = await api.get<Envelope<Array<{ id: number; name: string; unit: string; current_value: string | number; asset: number }>>>(
      API_PATHS.assets.meters,
      { params: { asset: assetId } },
    )
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  createMeter: async (payload: { asset: number; name: string; unit?: string }) => {
    const response = await api.post(API_PATHS.assets.meterCreate, payload)
    return unwrap(response.data)
  },

  addMeterReading: async (meterId: string | number, payload: { reading_value: number; notes?: string }) => {
    const response = await api.post(API_PATHS.assets.meterReading(meterId), payload)
    return unwrap(response.data)
  },

  listDocuments: async (assetId: string | number) => {
    const response = await api.get<Envelope<Array<{ id: number; title: string; doc_type: string; file_path: string; notes?: string }>>>(
      API_PATHS.assets.documents,
      { params: { asset: assetId } },
    )
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  createDocument: async (payload: { asset: number; title: string; file_path: string; doc_type?: string; notes?: string }) => {
    const response = await api.post(API_PATHS.assets.documentCreate, payload)
    return unwrap(response.data)
  },

  getConfiguration: async () => {
    const response = await api.get(API_PATHS.assets.configurationGet)
    return unwrap(response.data) as Record<string, unknown>
  },

  updateConfiguration: async (payload: Record<string, unknown>) => {
    const response = await api.post(API_PATHS.assets.configurationUpdate, payload)
    return unwrap(response.data) as Record<string, unknown>
  },

  reliabilityReport: async (days = 90) => {
    const response = await api.get(API_PATHS.assets.reliabilityReport, { params: { days } })
    return unwrap(response.data) as {
      period_days: number
      since: string
      asset_count: number
      results: Array<{
        asset_id: number
        asset_code: string
        name: string
        criticality?: string
        service_count: number
        corrective_count: number
        total_downtime_hours: number
        mttr_hours: number | null
        mtbf_days: number | null
      }>
    }
  },

  syncVehicles: async () => {
    const response = await api.post(API_PATHS.assets.vehicleSync, {})
    return unwrap(response.data) as { synced_count: number; assets: AssetRecord[] }
  },

  applyPlanTemplate: async (payload: {
    asset_ids: number[]
    template: Record<string, unknown>
  }) => {
    const response = await api.post(API_PATHS.assets.planApplyTemplate, payload)
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  listContracts: async (filters: { client_user?: string | number; status?: string } = {}) => {
    const params = new URLSearchParams()
    if (filters.client_user) params.append('client_user', String(filters.client_user))
    if (filters.status) params.append('status', filters.status)
    const response = await api.get<Envelope<AssetContractRecord[]>>(API_PATHS.assets.contracts, { params })
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  createContract: async (payload: Partial<AssetContractRecord> & { name: string; asset_ids?: number[] }) => {
    const response = await api.post<Envelope<AssetContractRecord>>(API_PATHS.assets.contractCreate, payload)
    return unwrap(response.data)
  },

  updateContract: async (id: string | number, payload: Partial<AssetContractRecord> & { asset_ids?: number[] }) => {
    const response = await api.post<Envelope<AssetContractRecord>>(API_PATHS.assets.contractUpdate(id), payload)
    return unwrap(response.data)
  },

  setContractAssets: async (id: string | number, asset_ids: number[]) => {
    const response = await api.post<Envelope<AssetContractRecord>>(API_PATHS.assets.contractSetAssets(id), {
      asset_ids,
    })
    return unwrap(response.data)
  },

  listBom: async (filters: { asset?: string | number; category?: string | number } = {}) => {
    const params = new URLSearchParams()
    if (filters.asset) params.append('asset', String(filters.asset))
    if (filters.category) params.append('category', String(filters.category))
    const response = await api.get<Envelope<AssetBomItemRecord[]>>(API_PATHS.assets.bom, { params })
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  createBomItem: async (payload: {
    asset?: number | null
    category?: number | null
    part_name: string
    sku?: string
    quantity?: number
    unit?: string
    unit_cost?: number | null
    is_critical?: boolean
  }) => {
    const response = await api.post<Envelope<AssetBomItemRecord>>(API_PATHS.assets.bomCreate, payload)
    return unwrap(response.data)
  },

  deleteBomItem: async (id: string | number) => {
    await api.delete(API_PATHS.assets.bomDelete(id))
  },

  listTransfers: async (filters: { asset?: string | number; status?: string } = {}) => {
    const params = new URLSearchParams()
    if (filters.asset) params.append('asset', String(filters.asset))
    if (filters.status) params.append('status', filters.status)
    const response = await api.get<Envelope<AssetTransferRecord[]>>(API_PATHS.assets.transfers, { params })
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  createTransfer: async (payload: {
    asset: number
    to_location?: number | null
    to_client_user?: number | null
    to_custodian_name?: string
    notes?: string
    complete?: boolean
  }) => {
    const response = await api.post<Envelope<AssetTransferRecord>>(API_PATHS.assets.transferCreate, payload)
    return unwrap(response.data)
  },

  completeTransfer: async (id: string | number) => {
    const response = await api.post<Envelope<AssetTransferRecord>>(API_PATHS.assets.transferComplete(id), {})
    return unwrap(response.data)
  },

  listIoTSources: async (assetId?: string | number) => {
    const params = new URLSearchParams()
    if (assetId) params.append('asset', String(assetId))
    const response = await api.get<Envelope<AssetIoTSourceRecord[]>>(API_PATHS.assets.iotSources, { params })
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  registerIoTSource: async (payload: {
    asset: number
    external_device_id: string
    provider?: string
    meter?: number | null
  }) => {
    const response = await api.post<Envelope<AssetIoTSourceRecord>>(API_PATHS.assets.iotRegister, payload)
    return unwrap(response.data)
  },

  ingestIoTReading: async (payload: {
    external_device_id: string
    value: number
    reading_at?: string
  }) => {
    const response = await api.post(API_PATHS.assets.iotIngest, payload)
    return unwrap(response.data)
  },

  listInspections: async (assetId: string | number) => {
    const response = await api.get<Envelope<AssetInspectionRecord[]>>(API_PATHS.assets.inspections, {
      params: { asset: assetId },
    })
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  createInspection: async (payload: {
    asset: number
    score: number
    condition_rating?: string
    findings?: string
    recommendations?: string
    inspector_name?: string
  }) => {
    const response = await api.post<Envelope<AssetInspectionRecord>>(API_PATHS.assets.inspectionCreate, payload)
    return unwrap(response.data)
  },

  getFinance: async (assetId: string | number) => {
    const response = await api.get<Envelope<{ asset: AssetRecord; finance: AssetFinanceSummary }>>(
      API_PATHS.assets.finance(assetId),
    )
    return unwrap(response.data)
  },

  updateFinance: async (
    assetId: string | number,
    payload: {
      purchase_cost?: number | null
      salvage_value?: number | null
      depreciation_method?: string
      useful_life_months?: number | null
      depreciation_start_date?: string | null
      finance_code?: string
      declining_rate_percent?: number | null
    },
  ) => {
    const response = await api.post<Envelope<{ asset: AssetRecord; finance: AssetFinanceSummary }>>(
      API_PATHS.assets.finance(assetId),
      payload,
    )
    return unwrap(response.data)
  },

  exportCsv: async () => {
    const response = await api.get(API_PATHS.assets.export, { responseType: 'blob' })
    return response.data as Blob
  },

  importCsv: async (file: File) => {
    const form = new FormData()
    form.append('file', file)
    const response = await api.post<Envelope<{ created: number; updated: number; error_count: number; errors: Array<{ row: number; error: string }> }>>(
      API_PATHS.assets.import,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return unwrap(response.data)
  },

  byCode: async (code: string) => {
    const response = await api.get<Envelope<AssetRecord>>(API_PATHS.assets.byCode, {
      params: { code },
    })
    return unwrap(response.data)
  },

  listMyAssets: async (includeHistory = true) => {
    const response = await api.get<Envelope<AssetRecord[]>>(API_PATHS.assets.myAssets, {
      params: { include_history: includeHistory },
    })
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  bulkGenerateOverdue: async (payload: { plan_ids?: number[]; limit?: number } = {}) => {
    const response = await api.post<
      Envelope<{
        generated_count: number
        generated: Array<{ plan_id: number; asset_id: number; occurrence_id: number; job_id: number | null }>
        error_count: number
      }>
    >(API_PATHS.assets.bulkGenerate, payload)
    return unwrap(response.data)
  },

  listReminders: async (filters: { status?: string; asset?: string | number } = {}) => {
    const params = new URLSearchParams()
    if (filters.status) params.append('status', filters.status)
    if (filters.asset) params.append('asset', String(filters.asset))
    const response = await api.get<Envelope<AssetReminderRecord[]>>(API_PATHS.assets.reminders, { params })
    const data = unwrap(response.data)
    return Array.isArray(data) ? data : []
  },

  scanReminders: async (withinDays = 14) => {
    const response = await api.post(API_PATHS.assets.reminderScan, { within_days: withinDays })
    return unwrap(response.data) as {
      scanned: number
      queued: number
      sent: number
      dispatched_sent?: number
      dispatched_failed?: number
      reminders: AssetReminderRecord[]
    }
  },

  dispatchReminders: async () => {
    const response = await api.post(API_PATHS.assets.reminderDispatch, {})
    return unwrap(response.data) as { dispatched_sent: number; dispatched_failed: number }
  },

  acknowledgeReminder: async (id: string | number) => {
    const response = await api.post(API_PATHS.assets.reminderAcknowledge(id), {})
    return unwrap(response.data)
  },
}

export interface AssetReminderRecord {
  id: number
  asset: number
  asset_code?: string
  asset_name?: string
  plan?: number | null
  plan_name?: string | null
  due_at?: string
  channel?: string
  title?: string
  message?: string
  status?: string
  scheduled_for?: string
  sent_at?: string | null
  last_error?: string
  attempt_count?: number
}

